/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { matchPath, useHistory, useLocation, useParams } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { callApi } from 'api/helpers/api.helpers';
import qs from 'query-string';
import draftApi from '../../api/draft.api';
import useProcedure from '../../procedure/useProcedure';
import { ConfigResourceProps } from '../../redux/enums/ConfigResourceProps';
import { useConfiguration } from '../../redux/features/configuration/useConfiguration';
import { getSearchPageByProcedure } from '../../shared/utils/objectUtils';
import { RoutePath } from '../../shared/enums/RoutePath';
import { showAlertSuccess } from '../../redux/features/alerts/alerts.slice';
import { sanitizeSubmission } from '../../shared/utils/submissionUtils';
import PaymentDialogState from '../components/dialog/enum/PaymentDialogState';
import paymentApi from '../api/payment.api';
import createAndSendPaymentForm from '../utils/createPaymentForm';
import { createMessage } from '../../redux/features/alerts/alerts.helper';

export default function usePayment() {
  const procedure = useProcedure();

  // fetch configuration elements
  const { resource: validationSchema } = useConfiguration(
    ConfigResourceProps.SUBMISSION_PAYMENT_VALIDATION_SCHEMA,
    procedure?.id,
  );

  const submissionUiSchema = useConfiguration(ConfigResourceProps.SUBMISSION_PAYMENT_UI_SCHEMA, procedure?.id);

  const [uiSchema, setUiSchema] = useState(undefined);
  useEffect(() => {
    if (submissionUiSchema.ready) setUiSchema(submissionUiSchema.resource);
  }, [submissionUiSchema.ready]);

  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const { submissionId } = useParams();

  const [submission, setSubmission] = useState(null);

  const [errors, setErrors] = useState([]);
  const [extraErrors, setExtraErrors] = useState({});
  const [skipValidation, setSkipValidation] = useState(false);

  const errorsRef = useRef(errors);
  const [workflowValidations, setWorkflowValidations] = useState({ sectionValidations: {}, isValid: false });

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const currentProcedure = useProcedure();

  // get Payment response retry setup

  const frontendCustomisation = useConfiguration(ConfigResourceProps.FRONTEND_CUSTOMISATION);
  const frontendConfig = frontendCustomisation.resource;

  const RETRY_PARAMS = {
    PAYMENT_CHECK_INTERVAL: frontendConfig?.payment?.interval || 2000,
    MAX_INTERVAL_COUNT: frontendConfig?.payment?.maxTryCount || 20,
  };

  const retryWrapper = {
    interval: null,
    paymentTryCount: 0,
  };

  const isPaymentPage = () => !!matchPath(location?.pathname, RoutePath.PROCEDURE_PAYMENT.path);

  useEffect(() => {
    return () => {
      resetInterval();
    };
  }, []);

  useEffect(() => {
    const options = {
      showAlertOnError: true,
      onSuccess: _submission => setSubmission(_submission),
      onError: () => history.replace(getSearchPageByProcedure(history)),
    };

    if (submissionId) {
      callApi(draftApi.getById, options)(submissionId);
    }
  }, [submissionId]);

  useEffect(() => {
    const _search = qs.parse(location.search);
    if (!_.isEmpty(_search?.state)) {
      setSkipValidation(true);
    } else if (skipValidation) {
      setSkipValidation(false);
    }
  }, [location.pathname, location.search]);

  const setErrorsWrapper = (error) => {
    setErrors(error);
    errorsRef.current = error;
  };

  const saveForm = useCallback(async () => {
    const sanitizedSubmission = sanitizeSubmission(submission, errorsRef.current);

    const updatedDraft = await callApi(draftApi.update)(submissionId, sanitizedSubmission);
    setSubmission(updatedDraft);
  }, [
    submission,
    setSubmission,
    submissionId,
    dispatch,
    history,
  ]);

  const onSaveAndClose = useCallback(async () => {
    await saveForm();
    const params = {
      procedure: submission?.procedure,
      ...(submission?.initialSubmissionProcedure && {
        initialSubmissionProcedure: submission?.initialSubmissionProcedure,
      }),
    };
    history.replace(RoutePath.generateRoutePath(RoutePath.PROCEDURE_SEARCH, params));
  }, [saveForm, history]);

  const onClose = useCallback(async () => {
    history.push('/');
  }, [history]);

  const onSave = useCallback(async () => {
    await saveForm();
    dispatch(showAlertSuccess(createMessage(t('draft.saved.successfully'))));
  }, [saveForm]);

  const setPopupState = ({ state }) => {
    const params = new URLSearchParams({ state });
    history.push({ ...history.location, search: params.toString() });
  };

  const submitPaymentRequest = async () => {
    setPopupState(PaymentDialogState.INITIALIZING);
    const paymentRequest = await paymentApi.buildPaymentRequest(currentProcedure?.id, submissionId);
    if (!paymentRequest) {
      setPopupState(PaymentDialogState.PRE_PAYMENT_FAILED);
      return;
    }
    const { data: paymentRequestData } = paymentRequest;
    createAndSendPaymentForm({
      id: submission?.id,
      action: paymentRequestData?.providerURL,
      map: paymentRequestData?.loadKeyValues,
    });
  };

  const setNonProviderPayment = async () => {
    await paymentApi.saveNonProvidePayment(currentProcedure?.id, submissionId);

    const params = {
      procedure: submission?.procedure,
      ...(submission?.initialSubmissionProcedure && {
        initialSubmissionProcedure: submission?.initialSubmissionProcedure,
      }),
    };
    history.replace(RoutePath.generateRoutePath(RoutePath.PROCEDURE_SEARCH, params));
  };

  const checkPaymentResult = async () => {
    retryWrapper.interval = setInterval(async () => {
      if (retryWrapper.paymentTryCount === RETRY_PARAMS.MAX_INTERVAL_COUNT) {
        setPopupState(PaymentDialogState.PAYMENT_TIMEOUT);
        resetInterval();
        return;
      }
      await _checkSubmissionHasPayment();
    }, RETRY_PARAMS.PAYMENT_CHECK_INTERVAL);
    await _checkSubmissionHasPayment();
  };

  const _checkSubmissionHasPayment = async () => {
    const currentSubmission = await paymentApi.getSubmission(submissionId);
    retryWrapper.paymentTryCount += 1;
    if (!_.isEmpty(currentSubmission?.data?.paymentResponse)) {
      const { paymentResponse: paymentResultData } = currentSubmission?.data;
      setPopupState(
        paymentResultData?.successful ? PaymentDialogState.PAYMENT_SUCCESS : PaymentDialogState.PAYMENT_FAILED,
      );
      resetInterval();
    }
  };

  const resetInterval = () => {
    clearInterval(retryWrapper.interval);
    retryWrapper.paymentTryCount = 0;
  };

  const onSendWithoutPayment = async () => {
    await callApi(draftApi.update)(submissionId, submission);
    await callApi(draftApi.sendToSign)(submissionId, true);
    history.push('/');
  };

  const toggleDrawer = useCallback(() => {
    setIsDrawerOpen(status => !status);
  }, [setIsDrawerOpen]);

  return {
    procedure,
    initialSubmissionProcedure: submission?.initialSubmissionProcedure,
    submission,
    setSubmission,
    validationSchema,
    uiSchema,
    setUiSchema,
    workflowValidations,
    setWorkflowValidations,
    errors,
    setErrors: setErrorsWrapper,
    extraErrors,
    setExtraErrors,
    onSave,
    onSaveAndClose,
    onClose,
    isDrawerOpen,
    toggleDrawer,
    isPaymentPage,
    submitPaymentRequest,
    checkPaymentResult,
    setNonProviderPayment,
    onSendWithoutPayment,
    resetInterval,
    skipValidation,
  };
}
